import { createContext, useContext } from 'react';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useGetAccountsByArea } from 'actions/accountManager/accounts';
import { useGetCurrentAccountUserRoles } from 'actions/accounts';
import { useGetCaAccountSettings } from 'actions/ca/accountSettings';
import { getCurrentAccount } from 'components/Organization/AccountManager/utils';
import { MANAGE_ORGANIZATION_AREA } from './constants';
import { AccountSettings, AccountUserRoles } from 'types/api/Account';

interface AccountUserRolesAndSettings {
  roles: AccountUserRoles[] | undefined;
  settings: AccountSettings | undefined;
}

/**
 * @description a context to provide information about an account user's roles within an account along with that account's specific settings, which can affect user permissions.
 */
export const AccountUserContext = createContext<AccountUserRolesAndSettings>({
  roles: undefined,
  settings: undefined
});
AccountUserContext.displayName = 'AccountUserContext';

export const AccountUserProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const currentUser = useCurrentUser();
  const { data = [], isLoading } = useGetAccountsByArea(
    MANAGE_ORGANIZATION_AREA
  );
  const accountId = getCurrentAccount(currentUser.current_account_id, data);
  const { data: settings } = useGetCaAccountSettings(accountId);
  const { data: roles } = useGetCurrentAccountUserRoles(accountId);

  if (isLoading) {
    return null;
  }

  return (
    <AccountUserContext.Provider
      value={{ roles: roles?.roles, settings: settings }}
    >
      {children}
    </AccountUserContext.Provider>
  );
};

export function useAccountUserContext() {
  const context = useContext(AccountUserContext);

  if (!context) {
    throw new Error('useAccountContext must be used within an AuthProvider');
  }

  return context;
}
